/**
* @ngdoc service
* @name tcApp.TimelineService
* @requires $window
* General Timelines/Graphs functions
*/

angular.module('tcApp')
.service('TimelineService', function($window, $mdMedia, GlobalService, dateParser, timeParser) {
	var d3 = $window.window.d3;
	var globalStrings = GlobalService.getGlobalStrings();
	var TimelineService = this;
	
	this.GraphType = 'GRAPH';
	this.AdminTimelineType = 'CP';
	this.EventTimelineType = 'EVENT';
	this.PathwayTimelineType = 'PATHWAY';
	this.OrderTimelineType = 'ORDER';
	this.ObsTimelineType = 'OP';
	
	this.Interval1M = '1M';
	this.Interval5M = '5M';
	this.Interval10M = '10M';
	this.Interval15M = '15M';
	this.Interval30M = '30M';
	this.Interval1H = '1H';
	this.Interval2H = '2H';
	this.Interval4H = '4H';
	this.Interval6H = '6H';
	this.Interval8H = '8H';
	this.Interval12H = '12H';
	this.Interval24H = '24H';
	this.IntervalWeek = 'W';
	this.IntervalMonth = 'M';
	this.IntervalYear = 'Y';

	var startDOW = GlobalService.configuration.date.startingDay;
	var timeWeekIntervalFunctions = {
		0: d3.timeWeek,
		1: d3.timeMonday,
		2: d3.timeTuesday,
		3: d3.timeWednesday,
		4: d3.timeThursday,
		5: d3.timeFriday,
		6: d3.timeSaturday
	};
	
	// Returns time header ticks function
	this.getAxisTicks = function(interval) {
		switch (interval) {
			case this.Interval1M:
				return d3.timeMinute.every(1);
			case this.Interval5M:
				return d3.timeMinute.every(5);
			case this.Interval10M:
				return d3.timeMinute.every(10);
			case this.Interval15M:
				return d3.timeMinute.every(15);
			case this.Interval30M:
				return d3.timeMinute.every(30);
			case this.Interval1H:
				return d3.timeHour.every(1);
			case this.Interval2H:
				return d3.timeHour.every(2);
			case this.Interval4H:
				return d3.timeHour.every(4);
			case this.Interval6H:
				return d3.timeHour.every(6);
			case this.Interval8H:
				return d3.timeHour.every(8);
			case this.Interval12H:
				return d3.timeHour.every(12);
			case this.Interval24H:
				return d3.timeDay.every(1);
			case this.IntervalWeek:
				var intervalFunction = timeWeekIntervalFunctions[startDOW];
				return intervalFunction.every(1);
			case this.IntervalMonth:
				return d3.timeMonth.every(1);
			case this.IntervalYear:
				return d3.timeYear.every(1);
			default:
				return 10;
		}
	};

	// Returns time header ticks in a given range from start to stop
	this.getTicksInRange = function(interval, start, end) {
		switch (interval) {
			case this.Interval1M:
				return d3.timeMinute.range(start, end, 1);
			case this.Interval5M:
				return d3.timeMinute.range(start, end, 5);
			case this.Interval10M:
				return d3.timeMinute.range(start, end, 10);
			case this.Interval15M:
				return d3.timeMinute.range(start, end, 15);
			case this.Interval30M:
				return d3.timeMinute.range(start, end, 30);
			case this.Interval1H:
				return d3.timeHour.range(start, end, 1);
			case this.Interval2H:
				return d3.timeHour.range(start, end, 2);
			case this.Interval4H:
				return d3.timeHour.range(start, end, 4);
			case this.Interval6H:
				return d3.timeHour.range(start, end, 6);
			case this.Interval8H:
				return d3.timeHour.range(start, end, 8);
			case this.Interval12H:
				return d3.timeHour.range(start, end, 12);
			case this.Interval24H:
				return d3.timeDay.range(start, end, 1);
			case this.IntervalWeek:
				var intervalFunction = timeWeekIntervalFunctions[startDOW];
				return intervalFunction.range(start, end, 1);
			case this.IntervalMonth:
				return d3.timeMonth.range(start, end, 1);
			case this.IntervalYear:
				return d3.timeYear.range(start, end, 1);
			default:
				return [];
		}
	};
	
	// Given an interval, returns [leftTick, rightTick], where leftTick is the first tick at or before
	// the specified time, and rightTick is the next tick after leftTick.  If interval matches the interval
	// of the current timeline being displayed, this corresponds to the start and end of the visual column
	// containing the specified time.
	this.getColumnTimes = function(time, interval) {
		if (angular.isUndefined(interval)) return [0, 0];
		
		var tickInterval = this.getAxisTicks(interval);
		if (!angular.isFunction(tickInterval)) return [0, 0];
		
		if (angular.isFunction(time.getTime)) time = time.getTime();
		var leftTick = tickInterval(time).getTime();
		
		// If the time is exactly on the tick, we'll get a zero interval size,
		// so increment the right tick time by 1 to get a non-zero interval size
		var rightTick = tickInterval.ceil(time + 1).getTime();
		
		return [leftTick, rightTick];
	};
	
	// Returns formatted label for time header ticks
	this.formatTick = function(JSTime, interval) {
		var date = new Date(JSTime);
		var shortMonthsArray = globalStrings['XMONTHSSHORT'].split(',');
		var daysArray = globalStrings['XDAYS'].split(',');
		switch (interval) {
			case this.Interval1M:
			case this.Interval5M:
			case this.Interval10M:
			case this.Interval15M:
			case this.Interval30M:
			case this.Interval1H:
			case this.Interval2H:
			case this.Interval4H:
			case this.Interval6H:
			case this.Interval8H:
			case this.Interval12H:
				return timeParser.prettyTime(date);
			case this.Interval24H:
				return daysArray[date.getDay()] + ' ' + date.getDate();
			case this.IntervalWeek:
				return shortMonthsArray[date.getMonth()] + ' ' + date.getDate();
			case this.IntervalMonth:
				return shortMonthsArray[date.getMonth()];
			case this.IntervalYear:
				return date.getFullYear();
		}
	};
	
	// Get TrakCare formatted timestamp (includes Thai, Hijra etc)
	this.getTimestamp = function(jsDateMs) {
		var time = new Date(jsDateMs);
		return dateParser.prettyDate(time) + ' ' + timeParser.prettyTime(time);
	};
	
	// returns translated Now caption
	this.getNowCaption = function() {
		return globalStrings['XNOW'];
	};
	
	// return dimensions to be used for aligning time headers/timeline items svg
	this.getTimelineDimensions = function(showRelativeDateMarkers, containerWidth, profileType, last) {
		//for stacked pathways remove top and bottom margins
		var margin;
		if (TimelineService.isStacked(profileType)) {
			margin = {top: -1, right: 70, bottom: (last ? 1 : 0), left: 80};
		} else {
			margin = {top: 10, right: 70, bottom: 10, left: 80};
		}
		var relativeTimeMargin = (showRelativeDateMarkers ? 5 : 3);
		var width = (containerWidth ? containerWidth : 960);
		var timeNowHeight = 12;
		var dimensions = {
			margin: margin,
			width: (width - margin.left - margin.right),
			timeHeaderHeight: 33,
			timeNowHeight: timeNowHeight,
			relativeTimeOffset: timeNowHeight + relativeTimeMargin,
			superHeadingOffset: 24 + relativeTimeMargin,
			axisOffset: 5 + relativeTimeMargin
		};
		
		return dimensions;
	};
	
	// Return ticks for time super headings
	this.getSuperHeadingTicks = function(interval) {
		switch (interval) {
			case this.Interval1M:
			case this.Interval5M:
			case this.Interval10M:
			case this.Interval15M:
			case this.Interval30M:
			case this.Interval1H:
			case this.Interval2H:
			case this.Interval4H:
			case this.Interval6H:
			case this.Interval8H:
			case this.Interval12H:
				return d3.timeDay.every(1);
			case this.Interval24H:
				return d3.timeMonth.every(1);
			case this.IntervalWeek:
				return d3.timeMonth.every(1);
			case this.IntervalMonth:
				return d3.timeYear.every(1);
			default:
				return 0;
		}
	};
	
	// Returns formatted label for time super headings
	this.formatSuperHeadingTick = function(JSTime, interval, isFixedHeader) {
		var date = new Date(JSTime);
		var shortMonthsArray = globalStrings['XMONTHSSHORT'].split(',');
		var year = date.getFullYear();
		var showYear = false;
		switch (interval) {
			case this.Interval1M:
			case this.Interval5M:
			case this.Interval10M:
			case this.Interval15M:
			case this.Interval30M:
			case this.Interval1H:
			case this.Interval2H:
			case this.Interval4H:
			case this.Interval6H:
			case this.Interval8H:
			case this.Interval12H:
				showYear = (((date.getMonth() === 11) && (date.getDate() === 31))
					|| ((date.getMonth() === 0) && (date.getDate() === 1)));
				return shortMonthsArray[date.getMonth()] + ' ' + date.getDate() + (showYear ?  ' ' + year : '');
			case this.Interval24H:
				showYear = ((date.getMonth() === 11) || (date.getMonth() === 0));
				if (isFixedHeader) {
					return shortMonthsArray[date.getMonth()] + ' ' + date.getDate();
				} else {
					return shortMonthsArray[date.getMonth()] + (showYear ?  ' ' + year : '');	
				}
			case this.IntervalWeek:
				if (isFixedHeader) {
					// convert date to start of first week out of window
					var day = (date.getDay() !== 0 ? date.getDay() : 7);
					if (day !== startDOW) {
						var dayDiff = (day - startDOW >= 0 ? day - startDOW : 7 + (day - startDOW));
						date.setHours(-24 * (dayDiff));
					}
					return shortMonthsArray[date.getMonth()] + ' ' + date.getDate() + ' ' + year;
				} else {
					showYear = ((date.getMonth() === 11) || (date.getMonth() === 0));
					return shortMonthsArray[date.getMonth()] + (showYear ?  ' ' + year : '');	
				}
			case this.IntervalMonth:
				if (isFixedHeader) {
					return shortMonthsArray[date.getMonth()] + ' ' + year;
				} else {
					return year;	
				}
		}	
	};
	
	// returns standard svg trantision duration for timeline and its items
	this.getSvgTransitionDuration = function() {
		return 500;	
	};
	
	// Find existing svg element or create and return a new element with given Id, type, and container
	this.getOrCreateSvgElementById = function(Id, elementType, container) {
		var elementSelection = container.select('#' + Id);
		if (elementSelection.empty()) {
			elementSelection = container.append(elementType).attr('id', Id);
		}
		
		return elementSelection;
	};
	
	// Find existing svg element or create and return a new element with given type and container
	this.getOrCreateSvgElementByType = function(elementType, container) {
		var elementSelection = container.select(elementType);
		if (elementSelection.empty()) {
			elementSelection = container.append(elementType);
		}
		
		return elementSelection;
	};
	
	// return format for relative time marker text
	this.formatRelativeTimeMarker = function(time, interval, baseTime, useGestation,
			isProblemTimeline, defaultRelativeMarkerInterval) {
		if (time === baseTime) {
			if (useGestation) return dateParser.prettyDate(new Date(time));	
			return globalStrings['XDOB'] + ': ' + dateParser.prettyDate(new Date(time));	
		} else if (isProblemTimeline) {
			var age;
			if (defaultRelativeMarkerInterval.value === this.IntervalYear) {
				baseTime = new Date(baseTime);
				age = time.getFullYear() - baseTime.getFullYear();
				if (time.getMonth() < baseTime.getMonth() ||
					((time.getMonth() === baseTime.getMonth()) && (time.getDate() < baseTime.getDate()))) {
					age--;
				}
			} else if (defaultRelativeMarkerInterval.value === this.IntervalMonth) {
				baseTime = new Date(baseTime);
				age = 12*(time.getFullYear() - baseTime.getFullYear()) + (time.getMonth() - baseTime.getMonth());
				if (time.getDate() < baseTime.getDate()) age--;
			} else {
				var ageUnrounded = (time - baseTime) / defaultRelativeMarkerInterval.JSTime;
				age = Math.floor(ageUnrounded * defaultRelativeMarkerInterval.relativeTimeFactor);
			}

			return age + ' ' + defaultRelativeMarkerInterval.unitDesc;
		} else {
			var offset = (time - baseTime) / interval.JSTime;
			return '+' + (offset * interval.relativeTimeFactor) + ' ' + interval.unitDesc; 
		}
	};
	
	// return whether using IE11, used to determine whether we need viewBox property for svg
	this.isIE11 = function() {
		return (!!$window.MSInputMethodContext && !!$window.document.documentMode);
	};
	
	// return class for time headers timeline item grid
	this.getTimeHeaderContainerClass = function(showLegend) {
		if (showLegend) {
			if ($mdMedia('gt-md')) {
				return 'flex-75';	
			} else {
				return 'flex-60';	
			}
		} else {
			return 'flex';	
		}
	};
	
	// return class for legend container
	this.getLegendContainerClass = function() {
		if ($mdMedia('gt-md')) {
			return 'flex-25';	
		} else {
			return 'flex-40';	
		}	
	};

	// return the icon name depending on if the timeline is showing
	this.getCollapseBarIcon = function(isCollapsed) { 
		if (isCollapsed) { 
			return 'system-chevron-right';
		} else {
			return 'system-chevron-down';
		}
	};

	// check if profile type contains stacked graphs
	this.isStacked = function(profileType) {
		if ((profileType === TimelineService.PathwayTimelineType) || (profileType === TimelineService.OrderTimelineType)) {
			return true;
		}
		return false;
	};

	// Label to display on the collapse bar
	this.getItemLabel = function(timelineItem) { 
		switch (timelineItem.profileType) {
			case this.GraphType:
			case this.EventTimelineType:
				return timelineItem.legendCaption;
			case this.OrderTimelineType: 
			case this.PathwayTimelineType:
				return timelineItem.superHeaderCaption;
			case this.AdminTimelineType: 
				return timelineItem.clinicalProfileData.items.PPDesc.defaultValue;
			case this.ObsTimelineType:
				return timelineItem.obsProfileData.items.PPDesc.defaultValue;
			default: 
				return '';
		}
	};
});
